import {
  Box, Grid, TextField, Stack, MenuItem, InputAdornment, Typography,
  Chip, Slider, Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useDispatch, useSelector } from 'react-redux';
import { updateMasterInfo } from '../../../../redux/pricingWizardSlice';
import { ORDER_SOURCE_GROUPS, CATEGORIES } from '../constants';
import { numberPrecision } from '../../../../common/util';
import IntegrationStackSection from './IntegrationStackSection';
import WizardSection from './WizardSection';

export default function MasterInfoCard() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const m = useSelector((s) => s.pricingWizard.masterInfo);

  const toggleSource = (src) => {
    const has = m.order_sources.includes(src);
    dispatch(updateMasterInfo({
      order_sources: has ? m.order_sources.filter((x) => x !== src) : [...m.order_sources, src],
    }));
  };

  const sharePct = m.prozo_wallet_share_pct ?? 100;
  const prozoVolume = numberPrecision((m.monthly_volume * sharePct) / 100, 0);

  return (
    <Stack spacing={2.25} sx={{ mt: 2.5 }}>

      {/* ── Source of Orders ── */}
      <WizardSection icon="🛒" title="Source of Orders" description="Channels the brand receives orders from.">
        <Stack spacing={1.25}>
          {ORDER_SOURCE_GROUPS.map((grp) => (
            <Box key={grp.group}>
              <Typography
                variant="overline"
                sx={{ fontSize: 9.5, fontWeight: 700, color: 'text.disabled', letterSpacing: 0.6, display: 'block', mb: 0.5 }}
              >
                {grp.group}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {grp.items.map((src) => {
                  const selected = m.order_sources.includes(src);
                  return (
                    <Chip
                      key={src}
                      label={src}
                      size="small"
                      icon={selected ? <CheckIcon sx={{ fontSize: '13px !important', color: 'inherit' }} /> : undefined}
                      onClick={() => toggleSource(src)}
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        borderRadius: '8px',
                        transition: 'all .15s',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                        color: selected ? 'primary.dark' : 'text.secondary',
                        border: 1,
                        borderColor: selected ? 'primary.main' : 'divider',
                        '&:hover': {
                          bgcolor: selected ? alpha(theme.palette.primary.main, 0.16) : alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </WizardSection>

      {/* ── Proship Integration · Tech Stack ── */}
      <WizardSection icon="🔌" title="Proship Integration · Tech Stack" description="Which tech stack will Proship integrate with?">
        <IntegrationStackSection />
      </WizardSection>

      {/* ── Category & Business Metrics ── */}
      <WizardSection icon="📊" title="Type of Enterprise and Business Metrics" description="Merchant size, order economics and shipment volume.">
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" fullWidth select label="Type of Enterprise"
              value={CATEGORIES.some((c) => c.value === m.category) ? m.category : ''}
              onChange={(e) => dispatch(updateMasterInfo({ category: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><CategoryOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }}
              SelectProps={{ renderValue: (val) => CATEGORIES.find((c) => c.value === val)?.label || '— select —' }}
            >
              <MenuItem value="">— select —</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  <Stack>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{c.label}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{c.sub}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" fullWidth type="number" label="Avg. Order Value (₹)"
              placeholder="e.g. 1499"
              value={m.aov || ''}
              onChange={(e) => dispatch(updateMasterInfo({ aov: Number(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small" fullWidth type="number" label="Avg. Units per Order"
              placeholder="e.g. 1.3" inputProps={{ step: 0.1 }}
              value={m.units_per_order || ''}
              onChange={(e) => dispatch(updateMasterInfo({ units_per_order: Number(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><Inventory2OutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              size="small" fullWidth type="number" label="Brand's Overall Shipment Volume"
              placeholder="e.g. 8000"
              value={m.monthly_volume || ''}
              onChange={(e) => dispatch(updateMasterInfo({ monthly_volume: Number(e.target.value) || 0 }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocalShippingOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Grid>

          {/* Wallet share given to Prozo — bounded control, not full-bleed */}
          <Grid item xs={12}>
            <Box sx={{ mt: 0.5, p: 2, borderRadius: 1.5, bgcolor: 'background.neutral', border: 1, borderColor: 'divider' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2.5}
                alignItems={{ md: 'center' }}
                divider={<Divider orientation="vertical" flexItem />}
              >
                {/* Metric readout */}
                <Box sx={{ minWidth: 190 }}>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, fontSize: 10 }}>
                    Wallet Share to Prozo
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 800, color: 'primary.main', lineHeight: 1.1 }}>{sharePct}%</Typography>
                  {m.monthly_volume > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>≈ {prozoVolume} shipments/mo to Prozo</Typography>
                  )}
                </Box>

                {/* Control — capped width so the slider doesn't span the page */}
                <Box sx={{ flex: 1, width: '100%', maxWidth: 520 }}>
                  <Slider
                    value={sharePct}
                    onChange={(_, v) => dispatch(updateMasterInfo({ prozo_wallet_share_pct: v }))}
                    min={0} max={100} step={5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    sx={{ '& .MuiSlider-thumb': { width: 18, height: 18 } }}
                  />
                  <Box sx={{ mt: 0.5, height: 10, display: 'flex', borderRadius: 99, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
                    <Box sx={{ width: `${sharePct}%`, bgcolor: 'primary.main', transition: 'width .2s' }} />
                    <Box sx={{ width: `${100 - sharePct}%`, bgcolor: 'grey.300', transition: 'width .2s' }} />
                  </Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Prozo · {sharePct}%</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400' }} />
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Other couriers · {100 - sharePct}%</Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </WizardSection>

    </Stack>
  );
}
